import React from "react";
import { CollectionModalProps } from "./types";
import Modal from "@components/atoms/Modal";
import CollectionListScreen from "./CollectionListScreen";
import { useTranslation } from "react-i18next";
import styled, { useTheme, DefaultTheme } from "styled-components/native";
import { Pressable } from "react-native";
import { VDBText } from "@components/atoms";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const CollectionModal = ({
  isOpen,
  onClose,
  itemIdArray,
  item,
  existingCollectionId,
  favItemIdArray,
  favoriteRemoved,
  collectionItemIdArray,
  collectionRemoved,
  fromSearchResultScreen,
  enableToastMessage,
  removeFavoriteItem,
  inWindow,
}: CollectionModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const typography = useTypographyStore();
  const insets = useSafeAreaInsets();

  const list = (
    <CollectionListScreen
      itemIdArray={itemIdArray}
      mItem={item}
      onClose={onClose}
      existingCollectionId={existingCollectionId}
      favItemIdArray={favItemIdArray}
      favoriteRemoved={favoriteRemoved ?? undefined}
      collectionItemIdArray={collectionItemIdArray}
      collectionRemoved={collectionRemoved ?? undefined}
      fromSearchResultScreen={fromSearchResultScreen}
      removeFavoriteItem={removeFavoriteItem ?? true}
      inWindow={inWindow}
    />
  );

  // In-window variant: used when this picker is opened from inside another RN
  // Modal (the fullscreen GalleryViewModal). Rendering a second RN Modal there
  // makes the inner sheet collapse when the create form's keyboard opens, so we
  // render the picker as an in-window overlay that lives in the same window.
  // The body padding matches the Modal path (16/16/16/32) so the create form's
  // in-window overlay (which cancels that padding) still lands edge-to-edge.
  if (inWindow) {
    if (!isOpen) {
      return null;
    }
    return (
      <OverlayRoot style={{ paddingBottom: insets.bottom }}>
        <Backdrop onPress={onClose} />
        <Sheet>
          <HeaderStrip>
            <VDBText
              textAlign="center"
              type={typography["H2-M"]}
              color={theme["core-05"]}
            >
              {t("global.text.chooseCollection")}
            </VDBText>
            <CloseButton
              testID="collection-modal-in-window-close"
              onPress={onClose}
            >
              <VDBIcon
                name={VDBIcons.CLOSE_CIRCLE}
                variant={IconsVariant.VDBCustom}
                size={24}
                color={theme["core-06"]}
              />
            </CloseButton>
          </HeaderStrip>
          <Divider />
          <Body>{list}</Body>
        </Sheet>
      </OverlayRoot>
    );
  }

  return (
    <Modal
      headerTitle={t("global.text.chooseCollection")}
      isOpen={isOpen}
      onClose={onClose}
      paddingLeft={16}
      paddingRight={16}
      paddingBottom={32}
      paddingTop={16}
      enableToastMessage={enableToastMessage ? enableToastMessage : false}
      skipKeyboardAware={true}
    >
      {list}
    </Modal>
  );
};

const OverlayRoot = styled.View`
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  z-index: 500;
  elevation: 40;
  justify-content: flex-end;
`;

const Backdrop = styled(Pressable)`
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  background-color: rgba(0, 0, 0, 0.5);
`;

const Sheet = styled.View`
  height: 92%;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  overflow: hidden;
`;

const HeaderStrip = styled.View`
  position: relative;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-02"]};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  padding: 16px;
`;

const Divider = styled.View`
  height: 1px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
`;

const Body = styled.View`
  flex: 1;
  padding: 16px 16px 32px 16px;
`;

const CloseButton = styled.TouchableOpacity`
  position: absolute;
  right: 12px;
`;

export default CollectionModal;
